/**
 * Badges: what they are called, and the rules that award them.
 *
 * Content, not engine. The rules name this game's species and marks, so they
 * live beside the list rather than in `src/game/progress.ts`. `App` re-runs
 * `earnedBadges` from scratch after anything that could change the answer,
 * so a badge added here is retroactively awarded to players who already
 * qualify.
 *
 * **Badge ids are stored in saves and on register entries — never rename or
 * reuse one.** Never add a badge for publishing, sharing or signing in; see
 * `AGENTS.md`.
 */

import type { SpeciesId } from './species';
import type { MutationId } from './weather';

export type BadgeId =
  | 'first-sprout'
  | 'first-tree'
  | 'full-plot'
  | 'grove'
  | 'botanist'
  | 'conservatory'
  | 'archivist'
  | 'patient'
  | 'returning'
  | 'week'
  | 'night'
  | 'marked'
  | 'four-skies'
  | 'harvest'
  | 'orchard';

export interface Badge {
  id: BadgeId;
  name: string;
  glyph: string;
  detail: string;
}

/**
 * Ordered roughly by when a player will earn them, because this list is also
 * the order they are shown in — an unearned badge should read as a next step,
 * not a wall.
 */
export const BADGES: Badge[] = [
  {
    id: 'first-sprout',
    name: 'First Sprout',
    glyph: '🌱',
    detail: 'Put your first seed in the ground.',
  },
  {
    id: 'first-tree',
    name: 'First Tree',
    glyph: '🌳',
    detail: 'Grow a specimen all the way to full height.',
  },
  {
    id: 'returning',
    name: 'Came Back',
    glyph: '🔁',
    detail: 'Return to your garden on another day.',
  },
  {
    id: 'patient',
    name: 'Patient Gardener',
    glyph: '⏳',
    detail: 'Have a tree reach full height while you were away.',
  },
  {
    id: 'marked',
    name: 'Marked by Weather',
    glyph: '🌦️',
    detail: 'Grow a specimen that carries the mark of the sky it grew under.',
  },
  {
    id: 'full-plot',
    name: 'Full Plot',
    glyph: '🪴',
    detail: 'Plant all twelve beds.',
  },
  {
    id: 'night',
    name: 'Night Gardener',
    glyph: '🌙',
    detail: 'Tend your garden between midnight and four in the morning.',
  },
  {
    id: 'harvest',
    name: 'First Harvest',
    glyph: '🧺',
    detail: 'Lift a fully grown specimen and bank its seedlings.',
  },
  {
    id: 'conservatory',
    name: 'Conservatory Key',
    glyph: '🌺',
    detail: 'Grow the desert rose — the one species never in the field draw.',
  },
  {
    id: 'grove',
    name: 'Grove',
    glyph: '🌴',
    detail: 'Bring all twelve beds to full height at once.',
  },
  {
    id: 'botanist',
    name: 'Botanist',
    glyph: '🌵',
    detail: 'Grow all six species.',
  },
  {
    id: 'orchard',
    name: 'Orchard',
    glyph: '🌾',
    detail: 'Harvest twenty-five specimens across the life of the garden.',
  },
  {
    id: 'week',
    name: 'Seven Days',
    glyph: '🗓️',
    detail: 'Visit your garden seven days running.',
  },
  {
    id: 'four-skies',
    name: 'Four Skies',
    glyph: '🌈',
    detail: 'Collect all four mutations — rain, storm, wadi cool and gold.',
  },
  {
    id: 'archivist',
    name: 'Archivist',
    glyph: '📖',
    detail: 'Uncover every card in the fact library.',
  },
];

export const BADGES_BY_ID = new Map(BADGES.map((b) => [b.id, b] as const));

/** Everything the badge rules need to know about the current session. */
export interface ProgressSnapshot {
  planted: number;
  capacity: number;
  trees: number;
  species: ReadonlySet<SpeciesId>;
  facts: number;
  totalFacts: number;
  streak: number;
  /** True once a tree has matured during an absence, ever. */
  maturedWhileAway: boolean;
  /** True once the gardener has been seen on two different days. */
  returned: boolean;
  /** Local hour, 0–23, of the most recent tend. */
  lastTendHour: number | null;
  /** Every mutation this gardener has ever brought to maturity. */
  mutations: ReadonlySet<MutationId>;
  /** Specimens lifted on this device, ever. */
  harvested: number;
}

export function earnedBadges(s: ProgressSnapshot): BadgeId[] {
  const out: BadgeId[] = [];
  if (s.planted > 0) out.push('first-sprout');
  if (s.trees > 0) out.push('first-tree');
  if (s.returned) out.push('returning');
  if (s.maturedWhileAway) out.push('patient');
  if (s.mutations.size > 0) out.push('marked');
  if (s.capacity > 0 && s.planted >= s.capacity) out.push('full-plot');
  if (s.lastTendHour !== null && s.lastTendHour < 4) out.push('night');
  if (s.species.has('desert-rose')) out.push('conservatory');
  if (s.capacity > 0 && s.trees >= s.capacity) out.push('grove');
  if (s.species.size >= 6) out.push('botanist');
  if (s.mutations.size >= 4) out.push('four-skies');
  if (s.harvested > 0) out.push('harvest');
  if (s.harvested >= 25) out.push('orchard');
  if (s.streak >= 7) out.push('week');
  if (s.totalFacts > 0 && s.facts >= s.totalFacts) out.push('archivist');
  return out;
}
